import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Users, UserCheck, UserX, CalendarClock, ClipboardList, Building2,
  Cake, Gift, PartyPopper, Megaphone, ArrowUpRight, TrendingUp, Sparkles, Pin, AlertTriangle, Info
} from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ icon: Icon, label, value, sub, tone = "blue", testid }) {
  const tones = {
    blue: "bg-red-50 text-[#DC2626]",
    green: "bg-emerald-50 text-emerald-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <Card className="p-5 rounded-xl border-slate-200/60 shadow-[0_4px_20px_rgba(37,99,235,0.04)] hover:shadow-[0_8px_30px_rgba(37,99,235,0.08)] hover:-translate-y-0.5 transition-all" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500 font-semibold">{label}</div>
          <div className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
      </div>
    </Card>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); } catch { return iso; }
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch((e) => setErr(e.message));
  }, []);

  if (err) return <div className="text-red-600">{err}</div>;
  if (!data) return <div className="text-slate-500">Loading dashboard…</div>;

  const isEmployee = user?.role === "employee";
  const isHR = user?.role === "hr";
  const isAdmin = user?.role === "admin";

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const bal = data.my_leave_balance || {};
  const allotted = bal.allotted || {};
  const used = bal.used || {};

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Pinned announcement banners */}
      <PinnedBanners announcements={data.announcements || []} />

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-6 sm:p-8">
        {/* Server-rack line-art motif — subtle, decorative only */}
        <ServerRackMotif />
        <div className="relative z-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">{data.company_name}</div>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              {greeting()}, {user?.name?.split(" ")[0]}
              <span className="ml-2 inline-block">👋</span>
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Here's what's happening across the team today.
            </p>
          </div>
          {isEmployee && (
            <Link to="/attendance">
              <Button className="bg-[#DC2626] hover:bg-[#B91C1C] rounded-lg h-10" data-testid="jump-attendance">
                <ArrowUpRight className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
                Go to Attendance
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stat grid — role aware */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Users} label="Headcount" value={data.total_employees} sub="Active employees" tone="blue" testid="stat-headcount" />
        <Stat icon={UserCheck} label="Present today" value={data.present_today} sub={`out of ${data.total_employees}`} tone="green" testid="stat-present" />
        <Stat icon={CalendarClock} label="On leave" value={data.on_leave_today} sub="Approved leaves today" tone="orange" testid="stat-onleave" />
        <Stat icon={ClipboardList} label="Pending approvals" value={data.pending_approvals} sub={isEmployee ? "Awaiting HR" : "Awaiting your review"} tone="red" testid="stat-pending" />
      </div>

      {/* DC Ops Pulse — brand widget */}
      <DcOpsPulse />

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left col — 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          {isEmployee && (
            <Card className="p-6 rounded-xl border-slate-200/60">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">My leave balance</h3>
                  <p className="text-xs text-slate-500">Financial year 2025-26</p>
                </div>
                <Link to="/leave" className="text-sm text-[#DC2626] font-medium hover:underline">Apply leave →</Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(allotted).map(([k, total]) => {
                  const u = used[k] || 0;
                  const remaining = Math.max(0, total - u);
                  const pct = total ? (u / total) * 100 : 0;
                  const label = k.replace(/_/g, " ");
                  return (
                    <div key={k} className="rounded-lg border border-slate-200 p-4">
                      <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 capitalize">{label}</div>
                      <div className="mt-2 flex items-baseline gap-1.5">
                        <span className="font-display text-2xl font-bold text-slate-900">{remaining}</span>
                        <span className="text-xs text-slate-500">/ {total} left</span>
                      </div>
                      <Progress value={pct} className="h-1.5 mt-3" />
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {(isHR || isAdmin) && (
            <Card className="p-6 rounded-xl border-slate-200/60">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">Department breakdown</h3>
                  <p className="text-xs text-slate-500">Head-of-department and coverage</p>
                </div>
                <Building2 className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
              </div>
              <div className="divide-y divide-slate-100">
                {data.departments.map((d) => (
                  <div key={d.id} className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{d.name}</div>
                      <div className="text-xs text-slate-500">Head: {d.head || "—"}</div>
                    </div>
                    <Badge variant="outline" className="text-xs">{d.name.split(" ")[0]}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Announcements */}
          <Card className="p-6 rounded-xl border-slate-200/60">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-[#DC2626]" strokeWidth={1.8} />
                <h3 className="font-display text-lg font-semibold text-slate-900">Company announcements</h3>
              </div>
            </div>
            <div className="space-y-3">
              {(data.announcements || []).filter(a => !a.pinned).length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No announcements right now.</div>
              ) : (data.announcements || []).filter(a => !a.pinned).map((a, i) => (
                <div key={a.id || i} className="rounded-lg border border-slate-100 bg-slate-50/50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm text-slate-900">{a.title}</div>
                    <span className="text-[11px] text-slate-500">{a.author}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed whitespace-pre-wrap">{a.body}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right col */}
        <div className="space-y-6">
          {/* Team on leave today */}
          <Card className="p-6 rounded-xl border-slate-200/60">
            <div className="flex items-center gap-2 mb-4">
              <PartyPopper className="h-4 w-4 text-orange-500" strokeWidth={1.8} />
              <h3 className="font-display text-lg font-semibold text-slate-900">On leave today</h3>
            </div>
            {data.team_on_leave.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                Everyone's in — nice.
              </div>
            ) : (
              <div className="space-y-3">
                {data.team_on_leave.map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-semibold text-slate-900">{l.employee_name}</div>
                      <div className="text-xs text-slate-500">{l.leave_type} · {fmtDate(l.from_date)} → {fmtDate(l.to_date)}</div>
                    </div>
                    <Badge className="bg-orange-50 text-orange-700 hover:bg-orange-50 border-0">{l.days}d</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Upcoming holidays */}
          <Card className="p-6 rounded-xl border-slate-200/60">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="h-4 w-4 text-emerald-600" strokeWidth={1.8} />
              <h3 className="font-display text-lg font-semibold text-slate-900">Upcoming holidays</h3>
            </div>
            <div className="space-y-3">
              {data.holidays.map((h) => (
                <div key={`${h.date}-${h.name}`} className="flex items-center justify-between text-sm">
                  <div>
                    <div className="font-semibold text-slate-900">{h.name}</div>
                    <div className="text-xs text-slate-500">{h.type}</div>
                  </div>
                  <div className="text-xs font-semibold text-[#DC2626]">{fmtDate(h.date)}</div>
                </div>
              ))}
            </div>
          </Card>

          {isAdmin && (
            <Card className="p-6 rounded-xl border-slate-200/60 bg-gradient-to-br from-blue-50 via-white to-white">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#DC2626]" strokeWidth={1.8} />
                <h3 className="font-display text-lg font-semibold text-slate-900">Admin insight</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Your Infrastructure & Cloud team has 4 engineers eligible for VMware VCP-DCV
                reimbursement this quarter. Consider spinning a training cohort.
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-emerald-600 font-semibold">
                <TrendingUp className="h-3.5 w-3.5" strokeWidth={2} />
                Attrition risk: Low
              </div>
            </Card>
          )}

          {isEmployee && data.my_profile && (
            <Card className="p-6 rounded-xl border-slate-200/60">
              <div className="flex items-center gap-2 mb-3">
                <Cake className="h-4 w-4 text-pink-500" strokeWidth={1.8} />
                <h3 className="font-display text-lg font-semibold text-slate-900">Your profile</h3>
              </div>
              <div className="text-sm text-slate-600 space-y-1.5">
                <div><span className="text-slate-400 uppercase text-[10px] tracking-widest">Code</span><br/><span className="font-semibold text-slate-900">{data.my_profile.employee_code}</span></div>
                <div><span className="text-slate-400 uppercase text-[10px] tracking-widest">Designation</span><br/><span className="font-semibold text-slate-900">{data.my_profile.designation}</span></div>
                <div><span className="text-slate-400 uppercase text-[10px] tracking-widest">Manager</span><br/><span className="font-semibold text-slate-900">{data.my_profile.reporting_manager || "—"}</span></div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function PinnedBanners({ announcements }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("dismissed_pins") || "[]")); }
    catch { return new Set(); }
  });
  const pinned = (announcements || []).filter(a => a.pinned && !dismissed.has(a.id));

  // Fire-and-forget: mark each shown announcement as read for this user
  useEffect(() => {
    (announcements || []).forEach(a => {
      if (a?.id) api.post(`/announcements/${a.id}/read`).catch(() => {});
    });
  }, [announcements]);

  if (pinned.length === 0) return null;

  const dismiss = (id) => {
    const next = new Set(dismissed); next.add(id); setDismissed(next);
    try { localStorage.setItem("dismissed_pins", JSON.stringify(Array.from(next))); }
    catch (e) { console.warn("Could not persist dismissed announcement", e); }
  };

  const tone = (sev) => {
    if (sev === "urgent") return { bar: "bg-red-50 border-[#DC2626]/30", icon: AlertTriangle, iconColor: "text-[#DC2626]", chip: "bg-[#DC2626] text-white" };
    if (sev === "warning") return { bar: "bg-amber-50 border-amber-300", icon: AlertTriangle, iconColor: "text-amber-700", chip: "bg-amber-500 text-white" };
    return { bar: "bg-blue-50 border-blue-200", icon: Info, iconColor: "text-blue-700", chip: "bg-blue-600 text-white" };
  };

  return (
    <div className="space-y-2" data-testid="pinned-banners">
      {pinned.map((a) => {
        const t = tone(a.severity);
        const Icon = t.icon;
        return (
          <div key={a.id} className={`relative rounded-xl border ${t.bar} p-4 pr-12 flex gap-3 items-start`} data-testid={`pin-${a.id}`}>
            <div className={`h-9 w-9 rounded-lg grid place-items-center bg-white/70 border border-white shrink-0`}>
              <Icon className={`h-5 w-5 ${t.iconColor}`} strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${t.chip}`}>{a.severity || "info"}</span>
                <div className="font-semibold text-slate-900">{a.title}</div>
                <Pin className="h-3.5 w-3.5 text-[#DC2626] fill-[#DC2626]" />
              </div>
              <div className="text-sm text-slate-700 mt-1 leading-relaxed whitespace-pre-wrap">{a.body}</div>
              <div className="text-[11px] text-slate-500 mt-1.5">— {a.author}</div>
            </div>
            <button onClick={() => dismiss(a.id)} className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-full text-slate-500 hover:bg-white/60 hover:text-slate-900" title="Dismiss" data-testid={`dismiss-${a.id}`}>
              <span className="text-lg leading-none">×</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}


/**
 * Subtle, brand-forward server-rack line-art placed behind the dashboard greeting.
 * Two racks with rack-unit dividers, blinking LED indicators, and a soft network-cable curve.
 * Pure inline SVG so it stays crisp at any size; hidden on very small viewports.
 */
function ServerRackMotif() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 hidden md:block select-none"
      data-testid="server-rack-motif"
    >
      {/* Faint grid dot texture in the top-right */}
      <svg
        className="absolute -right-4 -top-4 h-56 w-[420px] opacity-90"
        viewBox="0 0 420 240"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="rack-fade" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0F172A" stopOpacity="0" />
            <stop offset="55%" stopColor="#0F172A" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#0F172A" stopOpacity="0.12" />
          </linearGradient>
          <pattern id="dots" width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.8" fill="#94A3B8" opacity="0.35" />
          </pattern>
        </defs>

        {/* Ambient dot pattern for depth */}
        <rect x="0" y="0" width="420" height="240" fill="url(#dots)" opacity="0.5" />
        <rect x="0" y="0" width="420" height="240" fill="url(#rack-fade)" />

        {/* Network cable arcs — brand red, thin */}
        <path
          d="M 40 220 C 90 200, 130 200, 170 220 S 250 240, 300 210"
          stroke="#DC2626"
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.35"
          fill="none"
        />
        <path
          d="M 60 232 C 110 214, 150 216, 195 232 S 275 246, 320 224"
          stroke="#DC2626"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.22"
          fill="none"
        />

        {/* --- Server Rack A (taller) --- */}
        <g transform="translate(220, 30)">
          {/* Rack chassis */}
          <rect x="0" y="0" width="70" height="170" rx="3" stroke="#0F172A" strokeOpacity="0.28" strokeWidth="1.2" fill="none" />
          {/* Rack rails */}
          <line x1="6" y1="4" x2="6" y2="166" stroke="#0F172A" strokeOpacity="0.18" strokeWidth="0.8" />
          <line x1="64" y1="4" x2="64" y2="166" stroke="#0F172A" strokeOpacity="0.18" strokeWidth="0.8" />

          {/* 8 blade servers stacked */}
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
            const y = 10 + i * 19;
            return (
              <g key={i}>
                <rect x="10" y={y} width="50" height="14" rx="1.5" stroke="#0F172A" strokeOpacity="0.22" strokeWidth="0.9" fill="none" />
                {/* Front-panel vents */}
                <line x1="14" y1={y + 4} x2="14" y2={y + 10} stroke="#0F172A" strokeOpacity="0.15" strokeWidth="0.6" />
                <line x1="18" y1={y + 4} x2="18" y2={y + 10} stroke="#0F172A" strokeOpacity="0.15" strokeWidth="0.6" />
                <line x1="22" y1={y + 4} x2="22" y2={y + 10} stroke="#0F172A" strokeOpacity="0.15" strokeWidth="0.6" />
                {/* LEDs */}
                <circle cx="42" cy={y + 7} r="1.2" fill="#DC2626" opacity={i % 3 === 0 ? 0.9 : 0.35}>
                  {i % 3 === 0 && (
                    <animate attributeName="opacity" values="0.9;0.35;0.9" dur="2.4s" repeatCount="indefinite" />
                  )}
                </circle>
                <circle cx="48" cy={y + 7} r="1.2" fill="#10B981" opacity="0.7" />
                <circle cx="54" cy={y + 7} r="1.2" fill="#F59E0B" opacity={i % 4 === 0 ? 0.8 : 0.3} />
              </g>
            );
          })}
        </g>

        {/* --- Server Rack B (shorter, offset) --- */}
        <g transform="translate(310, 55)">
          <rect x="0" y="0" width="60" height="140" rx="3" stroke="#0F172A" strokeOpacity="0.24" strokeWidth="1.1" fill="none" />
          <line x1="5" y1="4" x2="5" y2="136" stroke="#0F172A" strokeOpacity="0.16" strokeWidth="0.7" />
          <line x1="55" y1="4" x2="55" y2="136" stroke="#0F172A" strokeOpacity="0.16" strokeWidth="0.7" />
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const y = 10 + i * 21;
            return (
              <g key={i}>
                <rect x="9" y={y} width="42" height="15" rx="1.5" stroke="#0F172A" strokeOpacity="0.2" strokeWidth="0.8" fill="none" />
                <line x1="13" y1={y + 4} x2="13" y2={y + 11} stroke="#0F172A" strokeOpacity="0.14" strokeWidth="0.6" />
                <line x1="17" y1={y + 4} x2="17" y2={y + 11} stroke="#0F172A" strokeOpacity="0.14" strokeWidth="0.6" />
                <circle cx="38" cy={y + 7.5} r="1.1" fill="#DC2626" opacity={i % 2 === 0 ? 0.8 : 0.25} />
                <circle cx="44" cy={y + 7.5} r="1.1" fill="#10B981" opacity="0.55" />
              </g>
            );
          })}
        </g>

        {/* Wi-Fi / signal fan above racks */}
        <g transform="translate(258, 18)" stroke="#DC2626" strokeLinecap="round" fill="none" opacity="0.45">
          <path d="M -6 6 Q 0 -2 6 6" strokeWidth="1.2" />
          <path d="M -10 10 Q 0 -6 10 10" strokeWidth="1" opacity="0.7" />
          <circle cx="0" cy="8" r="1.3" fill="#DC2626" stroke="none" />
        </g>
      </svg>
    </div>
  );
}


/**
 * DC Ops Pulse — a compact "is everything alive?" bar for engineers.
 * Polls /api/system/status every 20s, shows an animated heartbeat, service dots,
 * uptime, and a few real business signals.
 */
function DcOpsPulse() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/system/status");
      setS(data); setErr(false);
    } catch { setErr(true); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, []);

  const overallTone = (() => {
    if (err) return { chip: "bg-red-100 text-[#DC2626]", ring: "ring-red-500", dot: "bg-[#DC2626]", label: "Offline" };
    if (!s) return { chip: "bg-slate-100 text-slate-600", ring: "ring-slate-300", dot: "bg-slate-400", label: "Checking…" };
    if (s.overall === "Operational") return { chip: "bg-emerald-50 text-emerald-700", ring: "ring-emerald-500", dot: "bg-emerald-500", label: "All systems operational" };
    if (s.overall === "Degraded") return { chip: "bg-amber-50 text-amber-800", ring: "ring-amber-500", dot: "bg-amber-500", label: "Partial degradation" };
    return { chip: "bg-red-50 text-[#DC2626]", ring: "ring-red-500", dot: "bg-[#DC2626]", label: "Outage detected" };
  })();

  const uptime = s ? formatUptime(s.uptime_seconds) : "—";

  return (
    <div
      className="relative rounded-2xl border border-slate-200/60 bg-gradient-to-br from-[#0F172A] to-[#111827] text-slate-100 overflow-hidden"
      data-testid="dc-ops-pulse"
    >
      {/* Faint scanline / grid backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.08]"
        style={{ backgroundImage: "linear-gradient(#DC2626 1px, transparent 1px), linear-gradient(90deg, #DC2626 1px, transparent 1px)", backgroundSize: "24px 24px" }}
      />
      <div className="relative z-10 p-4 sm:p-5 flex flex-wrap items-center gap-4 sm:gap-6">
        {/* Heartbeat + status */}
        <div className="flex items-center gap-3 min-w-[220px]">
          <div className={`relative h-3 w-3 rounded-full ${overallTone.dot} ring-4 ring-inset ${overallTone.ring} ring-opacity-30`}>
            <span className={`absolute inset-0 rounded-full ${overallTone.dot} animate-ping opacity-75`} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">DC Ops Pulse</div>
            <div className={`text-sm font-semibold ${overallTone.chip.split(" ")[1]}`}>
              {overallTone.label}
            </div>
          </div>
        </div>

        {/* Service dots */}
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[280px]">
          {(s?.services || [1,2,3,4]).map((svc, i) => {
            if (typeof svc === "number") {
              // Loading placeholder
              return <div key={`ph-${svc}`} className="h-6 w-24 rounded bg-slate-700/50 animate-pulse" />;
            }
            const color = svc.status === "up" ? "bg-emerald-400"
              : svc.status === "degraded" ? "bg-amber-400" : "bg-red-500";
            return (
              <div key={svc.name} className="flex items-center gap-1.5 text-xs" data-testid={`svc-${svc.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className={`h-2 w-2 rounded-full ${color}`} />
                <span className="text-slate-300">{svc.name}</span>
                <span className="text-slate-500 font-mono text-[10px]">{svc.latency_ms}ms</span>
              </div>
            );
          })}
        </div>

        {/* Signals + uptime */}
        <div className="flex items-center gap-6 text-xs">
          <PulseSignal label="Active check-ins" value={s?.signals?.active_check_ins_10m ?? "—"} />
          <PulseSignal label="Open tickets" value={s?.signals?.open_tickets ?? "—"} accent={s?.signals?.open_tickets > 0} />
          <PulseSignal label="Uptime" value={uptime} mono />
        </div>
      </div>
    </div>
  );
}

function PulseSignal({ label, value, mono = false, accent = false }) {
  return (
    <div className="text-right">
      <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm font-semibold ${accent ? "text-[#FCA5A5]" : "text-slate-100"} ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function formatUptime(sec) {
  if (typeof sec !== "number") return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${sec % 60}s`;
}

